import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trophy, UserPlus, Users, History, FileText, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import FISImportV2Dialog from "@/components/FISImportV2Dialog";
import InviteAthletesDialog from "@/components/InviteAthletesDialog";
import CompetitionHistoryDialog from "@/components/CompetitionHistoryDialog";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { CompetitionEditDialog } from "@/components/CompetitionEditDialog";
import { useAuth } from "@/context/AuthContext";

export const DELETE_COMP_MSG = "Vuoi davvero eliminare questa gara? L'operazione non sarà disponibile nelle normali viste di CSP Lab.";

export default function Competitions() {
  const { user } = useAuth();
  const staff = user?.role !== "athlete";
  const [items, setItems] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);

  const doDelete = async () => {
    try { await api.delete(`/competitions/${toDelete.id}`); toast.success("Gara eliminata"); setItems((prev) => prev.filter((x) => x.id !== toDelete.id)); }
    catch { toast.error("Errore durante l'eliminazione"); }
  };
  const [form, setForm] = useState({ name: "", date: "", location: "", weapon: "fioretto", category: "" });

  const load = async () => {
    const [c, a] = await Promise.all([api.get("/competitions"), api.get("/athletes")]);
    setItems(c.data.sort((a,b) => (b.date||"").localeCompare(a.date||"")));
    setAthletes(a.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/competitions", form); toast.success("Gara creata"); setOpen(false); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Gare</h1>
          <p className="text-zinc-500 mt-1">Calendario e risultati.</p>
        </div>
        {staff && <div className="flex items-center gap-2 flex-wrap">
          <FISImportV2Dialog onDone={load} trigger={<Button variant="outline" className="h-12" data-testid="fis-import-btn"><FileText className="w-4 h-4 mr-2" />Importa calendario FIS</Button>} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-comp-btn" className="h-12 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuova gara</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova gara</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Nome</Label><Input data-testid="comp-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
                  <div><Label>Località</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Arma</Label><Input value={form.weapon} onChange={(e) => setForm({...form, weapon: e.target.value})} /></div>
                  <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="comp-save" onClick={save} className="bg-black hover:bg-zinc-800">Salva</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => {
          const inviteCount = (c.invited_athlete_ids || []).length;
          return (
            <div key={c.id} data-testid={`comp-${c.id}`} className="bg-white border border-zinc-200 rounded-md p-5 hover:border-zinc-400 transition-colors flex flex-col">
              <Link to={`/gare/${c.id}`} className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-zinc-400" />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{c.date}</span>
                </div>
                <div className="font-heading font-semibold text-lg leading-tight">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-2 font-mono">{c.location || "—"} · {c.weapon || "—"} · {c.category || "—"}</div>
              </Link>
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-zinc-600">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-zinc-400" />{inviteCount} convocati</span>
                  <span className="text-zinc-400">·</span>
                  <span>{c.results?.length || 0} risultati</span>
                </div>
                {staff && <>
                <InviteAthletesDialog
                  competition={c}
                  athletes={athletes}
                  onDone={load}
                  trigger={<Button variant="outline" size="sm" data-testid={`comp-invite-${c.id}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Convoca</Button>}
                />
                <CompetitionHistoryDialog
                  competitionId={c.id}
                  onDone={load}
                  trigger={<Button variant="outline" size="sm" data-testid={`comp-hist-${c.id}`}><History className="w-3.5 h-3.5" /></Button>}
                />
                <Button variant="outline" size="sm" data-testid={`comp-edit-${c.id}`} title="Modifica gara" onClick={() => setToEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="outline" size="sm" data-testid={`comp-delete-${c.id}`} className="text-zinc-500 hover:text-red-600 hover:border-red-300" onClick={() => setToDelete(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </>}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-zinc-300 rounded-md p-10 text-center">
            <Trophy className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
            <div className="text-sm text-zinc-600 mb-3">Nessuna gara in calendario.</div>
            {staff && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="empty-new-comp"><Plus className="w-4 h-4 mr-2" />Aggiungi la prima gara</Button>}
          </div>
        )}
      </div>
      <ConfirmDeleteDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title={`Eliminare "${toDelete?.name || ""}"?`} description={DELETE_COMP_MSG} onConfirm={doDelete} testId="delete-comp" />
      <CompetitionEditDialog competition={toEdit} open={!!toEdit} onOpenChange={(o) => !o && setToEdit(null)} onSaved={load} />
    </div>
  );
}
